// =============================================================================
// Origin UI — src/pages/evaluations/EvaluationFormPage.tsx
// View/review an evaluation by ID. Manager can edit their sections.
// Phase 7 — Year-End Evaluation & Probation Review module.
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { evaluationApi, EvaluationDetail, SaveResponseItem } from '@/services/evaluationApi';
import { useAuth } from '@/hooks/useAuth';
import { generateEvaluationPdf } from '@/services/evaluationPdfGenerator';
import styles from './evaluations.module.css';

export function EvaluationFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { roles } = useAuth();
  const [evaluation, setEvaluation] = useState<EvaluationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingChangesRef = useRef<Map<string, SaveResponseItem>>(new Map());

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await evaluationApi.getEvaluation(id);
      setEvaluation(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load evaluation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const scheduleAutoSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (!evaluation || pendingChangesRef.current.size === 0) return;
      try {
        setSaving(true);
        await evaluationApi.saveResponses(evaluation.evaluationId, Array.from(pendingChangesRef.current.values()));
        pendingChangesRef.current.clear();
        setLastSaved(new Date());
      } catch (e: any) {
        console.error('Auto-save failed:', e);
      } finally {
        setSaving(false);
      }
    }, 2000);
  }, [evaluation]);

  const handleResponseChange = useCallback((questionId: string, optionId: string | null, comment: string | null) => {
    pendingChangesRef.current.set(questionId, {
      templateQuestionId: questionId,
      ratingScaleOptionId: optionId,
      comment,
    });
    setEvaluation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map(s => ({
          ...s,
          questions: s.questions.map(q => {
            if (q.templateQuestionId !== questionId) return q;
            const selectedOption = q.ratingOptions?.find(o => o.ratingScaleOptionId === optionId);
            return {
              ...q,
              ratingScaleOptionId: optionId,
              selectedRatingLabel: selectedOption?.label ?? null,
              selectedRatingValue: selectedOption?.numericValue ?? null,
              comment: comment ?? q.comment,
            };
          }),
        })),
      };
    });
    scheduleAutoSave();
  }, [scheduleAutoSave]);

  function getIncompleteQuestions() {
    if (!evaluation) return [];
    return evaluation.sections
      .filter(s => s.respondent === 'Manager')
      .flatMap(s => s.questions)
      .filter(q => q.isRequired)
      .filter(q => {
        if (q.questionType === 'Rating') return !q.ratingScaleOptionId || !q.comment?.trim();
        if (q.questionType === 'FreeText') return !q.comment?.trim();
        return false;
      })
      .map(q => q.title);
  }

  const handleComplete = async () => {
    if (!evaluation) return;
    if (!confirm(`This will finalise the evaluation for ${evaluation.employeeName}. They will be able to see your rating and comments. Continue?`)) return;
    try {
      if (pendingChangesRef.current.size > 0) {
        await evaluationApi.saveResponses(evaluation.evaluationId, Array.from(pendingChangesRef.current.values()));
        pendingChangesRef.current.clear();
      }
      await evaluationApi.completeEvaluation(evaluation.evaluationId);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to complete');
    }
  };

  const handleReopen = async () => {
    if (!evaluation) return;
    if (!confirm('This will reopen the evaluation for editing. Continue?')) return;
    try {
      await evaluationApi.reopenEvaluation(evaluation.evaluationId);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to reopen');
    }
  };

  if (loading) return <div className={styles.page}><div className={styles.loading}>Loading evaluation...</div></div>;
  if (error) return <div className={styles.page}><div className={styles.errorBanner}>{error}</div></div>;
  if (!evaluation) return null;

  const isMidYear = evaluation.cycleType === 'MidYearReview';
  const isManagerEditable = isMidYear
    ? (evaluation.status === 'NotStarted' || evaluation.status === 'ManagerDraft')  // Mid-year: manager can start immediately
    : (evaluation.status === 'EmployeeSubmitted' || evaluation.status === 'ManagerDraft');
  const canComplete = isManagerEditable;
  const canReopen = roles.isHRAdmin && evaluation.cycleStatus !== 'Closed';



  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <h1 className={styles.pageTitle}>Evaluation: {evaluation.employeeName}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={styles.backBtn} onClick={() => generateEvaluationPdf(evaluation, 'Manager')}>Print PDF</button>
          <button className={styles.backBtn} onClick={() => navigate('/evaluations/team')}>← Back to Team</button>
        </div>
      </div>

      {/* Status banner */}
      <div className={`${styles.statusBanner} ${styles[`status_${evaluation.status}`] || ''}`}>
        <div className={styles.statusText}>
          <strong>Status:</strong> {formatStatus(evaluation.status)}
        </div>
        <div className={styles.saveIndicator}>
          {saving ? 'Saving...' : lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : ''}
        </div>
      </div>

      {/* Legend */}
      <div className={styles.card} style={{ padding: '8px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6B7885' }}>
            <span style={{ width: 12, height: 12, background: '#FF8300', borderRadius: 2, display: 'inline-block' }}></span> Manager Section
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6B7885' }}>
            <span style={{ width: 12, height: 12, background: '#00B0B9', borderRadius: 2, display: 'inline-block' }}></span> Employee Section
          </span>
        </div>
      </div>

      {/* Employee Info */}
      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Employee Information</h2>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Employee Name</span>
            <span className={styles.infoValue}>{evaluation.employeeName}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Job Title</span>
            <span className={styles.infoValue}>{evaluation.employeeJobTitle}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Department</span>
            <span className={styles.infoValue}>{evaluation.department}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Year in Review</span>
            <span className={styles.infoValue}>{evaluation.reviewYear}</span>
          </div>
        </div>
      </div>

      {/* Rating Definitions — show the primary scale's definitions at the top */}
      {(() => {
        const allOptions = evaluation.sections
          .flatMap(s => s.questions)
          .filter(q => q.ratingOptions && q.ratingOptions.length > 0)
          .map(q => q.ratingOptions!);
        const primaryScale = allOptions.length > 0
          ? allOptions.reduce((a, b) => a.length >= b.length ? a : b)
          : [];
        const defs = primaryScale.filter(o => o.description);
        if (defs.length === 0) return null;
        return (
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>Rating Definitions</h2>
            <div style={{ display: 'flex', border: '1px solid #E2E7EC', borderRadius: 8, overflow: 'hidden', margin: '16px 20px 4px' }}>
              {defs.map(opt => {
                const colorMap: Record<string, [string, string]> = {
                  'Outstanding': ['#00AA6E', '#E6F7F0'],
                  'Exceeds Expectations': ['#00B0B9', '#E0F7F8'],
                  'Meets Expectations': ['#FF8300', '#FFF3E0'],
                  'Needs Improvement': ['#F04B41', '#FDE8E7'],
                  'Unacceptable': ['#C33A30', '#FDE8E7'],
                };
                const [bg, lightBg] = colorMap[opt.label] || ['#6B7885', '#F1F4F6'];
                return (
                  <div key={opt.ratingScaleOptionId} style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid #E2E7EC' }}>
                    <div style={{ background: bg, color: '#fff', padding: '10px 8px', textAlign: 'center', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {opt.label}
                    </div>
                    <div style={{ padding: '10px 8px', background: lightBg, fontSize: 11, lineHeight: 1.5, color: '#4A5568', flex: 1 }}>
                      {opt.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Sections */}
      {evaluation.sections
        .filter(s => !s.isSystem)
        .map(section => {
          const isEmployeeSection = section.respondent === 'Employee';
          const isManagerSection = section.respondent === 'Manager';
          const isEditable = isManagerSection && isManagerEditable;

          return (
            <div key={section.templateSectionId} className={styles.card} style={{ borderLeft: isManagerSection ? '4px solid #FF8300' : isEmployeeSection ? '4px solid #00B0B9' : undefined }}>
              <h2 className={styles.sectionTitle}>
                {section.title}
                {isEmployeeSection && <span className={styles.respondentBadge}>Employee self-assessment</span>}
                {isManagerSection && <span className={styles.respondentBadge}>Manager review</span>}
              </h2>
              {section.description && <p className={styles.sectionDescription}>{section.description}</p>}

              {section.questions.map(q => (
                <div key={q.templateQuestionId} className={styles.questionBlock}>
                  <div className={styles.questionHeader}>
                    <h3 className={styles.questionTitle}>
                      {q.title}
                      {q.isRequired && isEditable && <span className={styles.required}>*</span>}
                    </h3>
                    {q.description && <p className={styles.questionDescription}>{q.description}</p>}
                  </div>

                  {q.questionType === 'Rating' && q.ratingOptions && (
                    <div className={styles.ratingGroup}>
                      {q.ratingOptions.map(opt => (
                        <label
                          key={opt.ratingScaleOptionId}
                          className={`${styles.ratingOption} ${q.ratingScaleOptionId === opt.ratingScaleOptionId ? styles.ratingSelected : ''} ${!isEditable ? styles.ratingDisabled : ''} ${q.isOverallRating && isEditable ? styles.overallRatingOption : ''}`}
                        >
                          <input
                            type="radio"
                            name={`rating_${q.templateQuestionId}`}
                            value={opt.ratingScaleOptionId}
                            checked={q.ratingScaleOptionId === opt.ratingScaleOptionId}
                            disabled={!isEditable}
                            onChange={() => handleResponseChange(q.templateQuestionId, opt.ratingScaleOptionId, q.comment)}
                            className={styles.ratingRadio}
                          />
                          <div>
                            <span className={styles.ratingLabel}>{opt.label}</span>
                            {q.isOverallRating && opt.description && (
                              <span className={styles.ratingDescription}>{opt.description}</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}

                  {q.questionType === 'FreeText' && (
                    <textarea
                      className={styles.freeText}
                      value={q.comment || ''}
                      disabled={!isEditable}
                      rows={6}
                      onChange={(e) => handleResponseChange(q.templateQuestionId, null, e.target.value)}
                    />
                  )}

                  {/* T3: Mandatory comment on every rating question */}
                  {q.questionType === 'Rating' && (
                    <textarea
                      className={styles.commentField}
                      value={q.comment || ''}
                      disabled={!isEditable}
                      placeholder={isEditable ? 'Why did you give this rating? *' : ''}
                      rows={3}
                      onChange={(e) => handleResponseChange(q.templateQuestionId, q.ratingScaleOptionId, e.target.value)}
                    />
                  )}

                  {/* Show selected rating as badge for read-only employee sections */}
                  {isEmployeeSection && q.selectedRatingLabel && (
                    <div className={styles.ratingBadge}>{q.selectedRatingLabel}</div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

      {/* Actions */}
      <div className={styles.actionBar}>
        {canComplete && (
          <>
            <button
              className={styles.submitBtn}
              onClick={() => {
                const incomplete = getIncompleteQuestions();
                if (incomplete.length > 0) { setValidationErrors(incomplete); return; }
                setValidationErrors([]);
                handleComplete();
              }}
            >
              Complete Review
            </button>
            {validationErrors.length > 0 && (
              <div style={{ marginTop: 12, padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, color: '#991B1B' }}>
                <strong>Please complete the following questions:</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                  {validationErrors.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
        {canReopen && (
          <button className={styles.reopenBtn} onClick={handleReopen}>
            Reopen Evaluation
          </button>
        )}
      </div>
    </div>
  );
}

function formatStatus(status: string): string {
  switch (status) {
    case 'NotStarted': return 'Not Started';
    case 'EmployeeDraft': return 'Employee Draft';
    case 'EmployeeSubmitted': return 'Submitted — Ready for Review';
    case 'ManagerDraft': return 'Manager Review in Progress';
    case 'ManagerCompleted': return 'Review Complete';
    case 'Acknowledged': return 'Acknowledged by Employee';
    default: return status;
  }
}
